import React, { useState } from 'react'

import {
  AlignItems,
  Body2,
  BorderStyle,
  Box,
  Button,
  ButtonColorVariant,
  ButtonSize,
  Color,
  Display,
  FlexDirection,
  FontWeight,
  Heading4,
  Icon,
  IconVariant,
  JustifyContent,
} from '@gousto-internal/citrus-react'
import { useDispatch } from 'react-redux'

import { startOneOffOrder } from 'actions/oneOffOrder'

import { useOrderConfirmation } from '../../OrderConfirmationContext'

type GoustoAnytimeBannerProps = {
  withBorder?: boolean
}

export const GoustoAnytimeBanner = ({ withBorder = false }: GoustoAnytimeBannerProps) => {
  const dispatch = useDispatch()
  const { skipBasketReset } = useOrderConfirmation()
  const [isStartingOneOffOrder, setIsStartingOneOffOrder] = useState(false)

  const handleBrowseRecipesClick = async () => {
    if (isStartingOneOffOrder) return

    setIsStartingOneOffOrder(true)
    skipBasketReset()
    try {
      await dispatch(startOneOffOrder() as any)
    } finally {
      setIsStartingOneOffOrder(false)
    }
  }

  return (
    <Box
      data-testid="gousto-anytime-order-confirmation-banner"
      bg={Color.White}
      borderColor={withBorder ? Color.ColdGrey_100 : undefined}
      borderStyle={withBorder ? BorderStyle.Solid : undefined}
      borderWidth={withBorder ? 0.5 : undefined}
      paddingV={withBorder ? 4 : undefined}
      paddingH={withBorder ? 4 : undefined}
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      gap={4}
      style={
        withBorder
          ? {
              borderRadius: '4px',
              boxShadow:
                '0px 1px 4px 0px rgba(33, 41, 48, 0.1), 0px 0px 2px 0px rgba(33, 41, 48, 0.05)',
            }
          : undefined
      }
    >
      <Box display={Display.Flex} alignItems={AlignItems.FlexStart} gap={3}>
        <Box
          display={Display.Flex}
          alignItems={AlignItems.Center}
          justifyContent={JustifyContent.Center}
          bg={Color.Primary_600}
          color={Color.White}
          width="48px"
          height="48px"
          flexShrink={0}
          style={{ borderRadius: '4px', marginTop: '4px' }}
        >
          <Icon name="gousto_g" size={10} variant={IconVariant.Inherit} />
        </Box>
        <Box display={Display.Flex} flexDirection={FlexDirection.Column} gap={1}>
          <Heading4 size={7} fontWeight={FontWeight.Bold} color={Color.ColdGrey_800}>
            Order your next one-off box
          </Heading4>
          <Body2 size={2} color={Color.ColdGrey_800}>
            No commitment needed. Choose your delivery day and we&apos;ll handle the rest.
          </Body2>
        </Box>
      </Box>

      <Box display={Display.Flex} alignItems={AlignItems.FlexStart} gap={1} wrap>
        <Box
          display={Display.Flex}
          alignItems={AlignItems.Center}
          bg={Color.NeutralGrey_50}
          paddingV={0.5}
          paddingH={2}
          gap={1}
          style={{ borderRadius: '4px' }}
        >
          <Icon name="tick" size={4} variant={IconVariant.Confirmation} />
          <Body2 size={0} color={Color.ColdGrey_800}>
            No commitment
          </Body2>
        </Box>
        <Box
          display={Display.Flex}
          alignItems={AlignItems.Center}
          bg={Color.NeutralGrey_50}
          paddingV={0.5}
          paddingH={2}
          gap={1}
          style={{ borderRadius: '4px' }}
        >
          <Icon name="tick" size={4} variant={IconVariant.Confirmation} />
          <Body2 size={0} color={Color.ColdGrey_800}>
            Next day delivery available
          </Body2>
        </Box>
      </Box>

      <Button
        colorVariant={ButtonColorVariant.Primary}
        size={ButtonSize.Medium}
        width="100%"
        onClick={handleBrowseRecipesClick}
        disabled={isStartingOneOffOrder}
      >
        Browse recipes
      </Button>
    </Box>
  )
}
